import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Box,
  Typography,
  Grid,
  Divider,
  FormHelperText,
  Paper,
  Chip,
  IconButton,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControlLabel,
  Card,
  CardContent,
  styled,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Plus, Upload, Trash2, Check, Clock, Tag, Phone, Map, Calendar, Info, AlignLeft, DollarSign, MessageSquare } from "lucide-react";
import Navbar from "../../components/Navbar";
import { toast } from "react-toastify";
import axios from "axios";
import { regions, locationsByRegion } from "../../utils/regions";
import Config from "../../Config";
import calculateDuration from "../../utils/CalculateDuration";
import calculateDiscount from "../../utils/CalculateDiscount";
import convertToBase64 from "../../utils/ConvertToBase64";
import { fetchCategories } from "../../requests/Categories/CategoryService";
import Loader from "../../components/Loader";
import { motion } from "framer-motion";

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  overflow: 'visible',
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '12px',
    '&:hover': {
      transform: 'none',
    },
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  background: '#f8f9fa',
  borderRadius: '16px',
  padding: '24px',
  marginBottom: '24px',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '16px',
    '&:hover': {
      transform: 'none',
    },
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #FF6C6C 0%, #FF8F8F 100%)',
  color: '#fff',
  padding: '12px 32px',
  borderRadius: '12px', 
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 4px 14px rgba(255, 108, 108, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(90deg, #FF5252 0%, #FF7575 100%)',
    boxShadow: '0 6px 20px rgba(255, 108, 108, 0.4)',
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 16px',
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  },
}));

const InputField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'white',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    },
    '&.Mui-focused': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(255,108,108,0.2)',
      '& fieldset': {
        borderColor: '#FF6C6C',
        borderWidth: '2px',
      }
    }
  },
  '& .MuiInputLabel-root': {
    '&.Mui-focused': {
      color: '#FF6C6C'
    }
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiOutlinedInput-root': {
      '&:hover': {
        transform: 'none',
      },
      '&.Mui-focused': {
        transform: 'none',
      }
    }
  }
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    borderRadius: '12px',
    backgroundColor: 'white',
    padding: '12px 14px',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
  '&.Mui-focused': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(255,108,108,0.2)',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '12px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FF6C6C',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#FF6C6C',
  },
  [theme.breakpoints.down('sm')]: {
    '&:hover': {
      transform: 'none',
    },
    '&.Mui-focused': {
      transform: 'none',
    },
    '& .MuiSelect-select': {
      padding: '10px 12px',
    }
  }
}));

const StepIcon = styled(Box)(({ completed }) => ({
  width: '40px',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: completed ? '#FF6C6C' : '#f1f1f1',
  color: completed ? 'white' : '#666',
  borderRadius: '50%',
  marginRight: '16px',
  transition: 'all 0.3s ease',
}));

const ImageUploadContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '32px',
  borderRadius: '16px',
  border: '2px dashed #e0e0e0',
  backgroundColor: '#f8f9fa',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#FF6C6C',
    backgroundColor: '#fff9f9',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '20px 12px',
    borderRadius: '12px',
  }
}));

const PageContainer = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f6f9fc 0%, #f0f4f8 100%)',
  minHeight: '100vh',
  padding: '24px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px 8px',
  }
}));

const CreatePremiumDeal = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    title: "",
    category: [],
    end_date: "",
    start_date: new Date().toISOString().slice(0, 10),
    redemption_instructions: "",
    phone_number: "",
    original_price: "",
    discounted_price: "",
    location: [{ region: "", location: "", customLocation: "" }],
    description: "",
    sms_content: "",
    image: null,
    isNationwide: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);

  // Steps for stepper
  const steps = [
    { 
      label: 'Basic Information', 
      icon: <Info size={20} />,
      description: 'Enter deal title, category and set the duration'
    },
    { 
      label: 'Location & Price', 
      icon: <Map size={20} />,
      description: 'Specify availability locations and pricing details'
    },
    { 
      label: 'Deal Details', 
      icon: <AlignLeft size={20} />,
      description: 'Enter description, redemption instructions and phone number'
    },
    { 
      label: 'Finalize & Publish', 
      icon: <Check size={20} />,
      description: 'Review deal and create'
    },
  ];

  // Handle step navigation
  const handleNext = () => {
    if (validateStepFields(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      toast.error("Please fix the errors before proceeding");
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Function to validate fields at each step
  const validateStepFields = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      if (!formData.title.trim()) newErrors.title = "Title is required";
      if (formData.category.length === 0) newErrors.category = "At least one category is required";
      if (!formData.end_date) newErrors.end_date = "End date is required";
    } 
    else if (step === 1) {
      if (!formData.isNationwide) {
        const invalidLocations = formData.location.some(loc => 
          !loc.region || 
          !loc.location || 
          (loc.location === 'Other' && !loc.customLocation)
        );
        if (invalidLocations) {
          newErrors.location = "All locations must be properly filled";
        }
      }
      if (!formData.original_price) newErrors.original_price = "Original price is required";
      if (!formData.discounted_price) newErrors.discounted_price = "Discounted price is required";
    }
    else if (step === 2) {
      if (!formData.phone_number) newErrors.phone_number = "Phone number is required";
      if (formData.phone_number && !/^\d{9}$/.test(formData.phone_number)) {
        newErrors.phone_number = "Phone number must be exactly 9 digits (after 233)";
      }
      if (!formData.description.trim()) newErrors.description = "Description is required";
      if (!formData.sms_content.trim()) newErrors.sms_content = "SMS content is required";
    }
    else if (step === 3) {
      if (!formData.image) {
        newErrors.image = "Deal image is required";
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(()=>{
    const fetchActiveCategories = async()=>{
      fetchCategories(setCategories, setLoading, setError)
    }
    fetchActiveCategories();
  },[])

  const handleCategoryChange = (event) => {
    setFormData({
      ...formData,
      category: event.target.value,
    });
  };

  const addLocation = () => {
    if (formData.location.length >= 5) {
      toast.warning("Maximum of 5 locations allowed");
      return;
    }
    setFormData({
      ...formData,
      location: [...formData.location, { region: "", location: "", customLocation: "" }],
    });
  };

  const removeLocation = (index) => {
    const updatedLocations = [...formData.location];
    updatedLocations.splice(index, 1);
    setFormData({
      ...formData,
      location: updatedLocations,
    });
  };

  const handleLocationChange = (index, field, value) => {
    const updatedLocations = [...formData.location];
    
    if (field === 'region') {
      console.log('Selected region:', value);
      console.log('Available locations:', locationsByRegion[value]);
    }
    
    updatedLocations[index] = {
      ...updatedLocations[index],
      [field]: value,
      // Reset location and customLocation when region changes
      ...(field === 'region' && { location: '', customLocation: '' }),
      // Reset customLocation when location changes to non-Other
      ...(field === 'location' && value !== 'Other' && { customLocation: '' }),
    };
    
    setFormData({
      ...formData,
      location: updatedLocations,
    });
  };

  const handleCustomLocationChange = (index, value) => {
    const updatedLocations = [...formData.location];
    updatedLocations[index] = {
      ...updatedLocations[index],
      customLocation: value,
    };
    setFormData({
      ...formData,
      location: updatedLocations,
    });
  };

  const handleNationwideChange = (event) => {
    setFormData({
      ...formData,
      isNationwide: event.target.checked,
      location: event.target.checked ? [] : [{ region: "", location: "", customLocation: "" }],
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (2MB = 2 * 1024 * 1024 bytes)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error("Image size must not exceed 2MB");
        return;
      }

      // Convert to base64 and set both the file and preview
      convertToBase64(file)
        .then(base64String => {
          setFormData({
            ...formData,
            image: base64String, // Store base64 string instead of file
          });
          setImagePreview(URL.createObjectURL(file));
        })
        .catch(error => {
          console.error("Error converting image to base64:", error);
          toast.error("Failed to process image");
        });
    }
  };

  const removeImage = () => {
    setFormData({
      ...formData,
      image: null,
    });
    setImagePreview(null);
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors = {};
    
    if (!validateStepFields(0)) return false;
    if (!validateStepFields(1)) return false;
    if (!validateStepFields(2)) return false;
    if (!validateStepFields(3)) return false;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateDiscountedPrice = (original, discounted) => {
    const originalPrice = parseFloat(original);
    const discountedPrice = parseFloat(discounted);
    
    if (discountedPrice > originalPrice) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }
    
    setLoading(true);
    
    try {
      const formattedStartDate = `${formData.start_date} 00:00:00`;
      const formattedEndDate = `${formData.end_date} 23:59:59`;
      
      const requestBody = {
        title: formData.title,
        category: formData.category,
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        redemption_instructions: formData.redemption_instructions,
        phone_number: `233${formData.phone_number}`,
        original_price: Number(formData.original_price).toFixed(2),
        discounted_price: Number(formData.discounted_price).toFixed(2),
        location: formData.isNationwide 
          ? [{ region: "all", location: "all" }]
          : formData.location.map(loc => ({
              region: loc.region,
              location: loc.location === 'Other' ? loc.customLocation : loc.location
            })),
        description: formData.description,
        sms_content: formData.sms_content,
        deal_image: formData.image
      };
      
      // API request
      const response = await axios.post(
        `${Config.baseUrl}/only/deals`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.data.data;
      console.log("data", data.status_code);
      
      if(data.status_code === "001"){
        toast.success(data.reason || "Premium deal created successfully!");
        window.location.href = "/premium-deals";
      }
      else{
        toast.error(data.reason || "Failed to create premium deal");
      }
    } catch (error) {
      console.error("Error creating premium deal:", error);
      toast.error(error.response?.data?.reason || "Failed to create premium deal");
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderBasicInfo();
      case 1:
        return renderLocationPrice();
      case 2:
        return renderDealDetails();
      case 3:
        return renderFinalize();
      default:
        return null;
    }
  };

  const renderBasicInfo = () => {
    return (
      <StyledCard 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {!isMobile && (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </>
          )}

          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12}>
              <InputField
                fullWidth
                label="Deal Title"
                name="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                error={!!errors.title}
                helperText={errors.title}
                required
                InputProps={{
                  startAdornment: <Tag size={18} style={{ marginRight: 10, color: '#797D7F' }} />,
                }}
              />
            </Grid>

            {/* Category */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.category}>
                <InputLabel id="category-label" sx={{ '&.Mui-focused': { color: '#FF6C6C' } }}>
                  Categories
                </InputLabel>
                <StyledSelect
                  labelId="category-label"
                  multiple
                  value={formData.category}
                  onChange={handleCategoryChange}
                  input={<OutlinedInput label="Categories" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const category = categories.find(cat => cat.name === value);
                        return (
                          <Chip 
                            key={value} 
                            label={category ? category.name : value} 
                            size="small"
                            sx={{ 
                              bgcolor: 'rgba(255, 108, 108, 0.1)',
                              color: '#FF6C6C',
                              fontWeight: 500
                            }} 
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.name}>
                      {category.name}
                    </MenuItem>
                  ))}
                </StyledSelect>
                {errors.category && <FormHelperText error>{errors.category}</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Date Fields */}
            <Grid item xs={12}>
              <FormSection
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Calendar size={isMobile ? 16 : 20} style={{ color: '#FF6C6C' }} />
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="600" 
                    sx={{ ml: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}
                  >
                    Deal Duration
                  </Typography>
                </Box>
                
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} md={6}>
                    <InputField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={formData.start_date}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputField
                      fullWidth
                      label="End Date"
                      type="date"
                      value={formData.end_date}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      error={!!errors.end_date}
                      helperText={errors.end_date}
                      required
                      inputProps={{ min: formData.start_date }}
                    />
                  </Grid>
                </Grid>
                
                {formData.start_date && formData.end_date && (
                  <Box sx={{ 
                    mt: 2, 
                    p: { xs: 1.5, md: 2 }, 
                    bgcolor: 'rgba(255, 108, 108, 0.08)', 
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <Clock size={isMobile ? 16 : 18} style={{ color: '#FF6C6C' }} />
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ ml: 1, fontSize: { xs: '0.8rem', md: '0.875rem' } }}
                    >
                      Deal will be active for <strong>{calculateDuration(formData.start_date, formData.end_date)}</strong> days
                    </Typography>
                  </Box>
                )}
              </FormSection>
            </Grid>
          </Grid>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            mt: { xs: 2, md: 3 }
          }}>
            <GradientButton 
              onClick={handleNext} 
              endIcon={<motion.div animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>→</motion.div>}
              fullWidth={isMobile}
            >
              Continue to Location & Price
            </GradientButton>
          </Box>
        </CardContent>
      </StyledCard>
    );
  };

  // Step 2: Location & Price
  const renderLocationPrice = () => {
    return (
      <StyledCard 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {!isMobile && (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Location & Price
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </>
          )}

          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Locations Section */}
            <Grid item xs={12}>
              <FormSection
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Map size={isMobile ? 16 : 20} style={{ color: '#FF6C6C' }} />
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="600" 
                    sx={{ ml: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}
                  >
                    Deal Locations
                  </Typography>
                </Box>
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isNationwide}
                      onChange={handleNationwideChange}
                      sx={{
                        '&.Mui-checked': { color: '#FF6C6C' },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      Nationwide (Available Everywhere)
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />

                {!formData.isNationwide && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                      <Button
                        startIcon={<Plus size={isMobile ? 16 : 18} />}
                        onClick={addLocation}
                        variant="outlined"
                        size={isMobile ? "small" : "medium"}
                        sx={{ 
                          borderColor: '#FF6C6C', 
                          color: '#FF6C6C', 
                          textTransform: 'none',
                          borderRadius: '8px',
                          fontSize: { xs: '0.75rem', md: '0.875rem' }
                        }}
                      >
                        Add Location
                      </Button>
                    </Box>

                    {formData.location.map((loc, index) => (
                      <Box 
                        key={index} 
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                        sx={{ 
                          mb: 2, 
                          p: { xs: 2, md: 3 }, 
                          border: '1px solid #eaeaea', 
                          borderRadius: 3,
                          position: 'relative',
                          backgroundColor: 'white',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                          '&:hover': {
                            boxShadow: '0 6px 16px rgba(0,0,0,0.06)',
                          }
                        }}
                      >
                        <Grid container spacing={{ xs: 2, md: 2 }}>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                              <InputLabel sx={{ '&.Mui-focused': { color: '#FF6C6C' } }}>
                                Region
                              </InputLabel>
                              <StyledSelect
                                value={loc.region}
                                onChange={(e) => handleLocationChange(index, 'region', e.target.value)}
                                label="Region"
                              >
                                {regions.map((region) => (
                                  <MenuItem key={region.value} value={region.value}>
                                    {region.name}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth disabled={!loc.region} size={isMobile ? "small" : "medium"}>
                              <InputLabel sx={{ '&.Mui-focused': { color: '#FF6C6C' } }}>
                                Location
                              </InputLabel>
                              <StyledSelect
                                value={loc.location}
                                onChange={(e) => handleLocationChange(index, 'location', e.target.value)}
                                label="Location"
                              >
                                {locationsByRegion[loc.region]?.map((location) => (
                                  <MenuItem key={location} value={location}>
                                    {location}
                                  </MenuItem>
                                ))}
                                <MenuItem value="Other">Other (Specify)</MenuItem>
                              </StyledSelect>
                            </FormControl>
                          </Grid>
                          
                          {/* Custom Location Input */}
                          {loc.location === 'Other' && (
                            <Grid item xs={12}>
                              <InputField
                                fullWidth
                                label="Specify Location"
                                value={loc.customLocation}
                                onChange={(e) => handleCustomLocationChange(index, e.target.value)}
                                size={isMobile ? "small" : "medium"}
                              />
                            </Grid>
                          )}
                        </Grid>
                        
                        {formData.location.length > 1 && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeLocation(index)}
                            sx={{
                              position: 'absolute',
                              top: { xs: '4px', md: '8px' },
                              right: { xs: '4px', md: '8px' },
                              bgcolor: 'white',
                              '&:hover': {
                                bgcolor: '#ffeeee'
                              }
                            }}
                          >
                            <Trash2 size={isMobile ? 16 : 18} />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </>
                )}
              </FormSection>
            </Grid>
            
            {/* Pricing Fields */}
            <Grid item xs={12}>
              <FormSection
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DollarSign size={isMobile ? 16 : 20} style={{ color: '#FF6C6C' }} />
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="600" 
                    sx={{ ml: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}
                  >
                    Deal Pricing
                  </Typography>
                </Box>
                
                <Grid container spacing={{ xs: 2, md: 3 }}>
                  <Grid item xs={12} md={6}>
                    <InputField
                      fullWidth
                      label="Original Price (GHS)"
                      value={formData.original_price}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d.]/g, '');
                        setFormData({ ...formData, original_price: value });
                      }}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>₵</Typography>,
                      }}
                      error={!!errors.original_price}
                      helperText={errors.original_price}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InputField
                      fullWidth
                      label="Discounted Price (GHS)"
                      value={formData.discounted_price}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d.]/g, '');
                        if (!value || validateDiscountedPrice(formData.original_price, value)) {
                          setFormData({ ...formData, discounted_price: value });
                        } else {
                          toast.error("Discounted price cannot be greater than original price");
                        }
                      }}
                      InputProps={{
                        startAdornment: <Typography sx={{ mr: 1 }}>₵</Typography>,
                      }}
                      error={!!errors.discounted_price}
                      helperText={errors.discounted_price}
                      required
                      size={isMobile ? "small" : "medium"}
                    />
                  </Grid>
                </Grid>
                
                {formData.original_price && formData.discounted_price && (
                  <Box sx={{ 
                    mt: 2, 
                    p: { xs: 1.5, md: 2 }, 
                    bgcolor: 'rgba(255, 108, 108, 0.08)', 
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center' 
                  }}>
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      color="#FF6C6C"
                      fontWeight={600}
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      {calculateDiscount(formData.original_price, formData.discounted_price)}% OFF
                    </Typography>
                  </Box>
                )}
              </FormSection>
            </Grid>
          </Grid>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: { xs: 2, md: 3 },
            flexDirection: { xs: isMobile ? 'column' : 'row', md: 'row' },
            gap: { xs: 2, md: 0 }
          }}>
            <Button 
              onClick={handleBack}
              sx={{ 
                color: '#666', 
                textTransform: 'none',
                order: { xs: isMobile ? 2 : 1, md: 1 }
              }}
            >
              Back
            </Button>
            <GradientButton 
              onClick={handleNext}
              endIcon={<motion.div animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>→</motion.div>}
              fullWidth={isMobile}
              sx={{ order: { xs: isMobile ? 1 : 2, md: 2 } }}
            >
              Continue to Deal Details
            </GradientButton>
          </Box>
        </CardContent>
      </StyledCard>
    );
  };

  // Step 3: Deal Details
  const renderDealDetails = () => {
    return (
      <StyledCard 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {!isMobile && (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Deal Details
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </>
          )}

          <Grid container spacing={{ xs: 2, md: 3 }}>
            {/* Phone Number */}
            <Grid item xs={12} md={6}>
              <InputField
                fullWidth
                label="Phone Number"
                value={formData.phone_number}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  setFormData({ ...formData, phone_number: value.slice(0, 9) });
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Phone size={isMobile ? 16 : 18} style={{ marginRight: 5, color: '#797D7F' }} />
                      <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>233</Typography>
                    </Box>
                  ),
                }}
                error={!!errors.phone_number}
                helperText={errors.phone_number || "Format: 233XXXXXXXXX"}
                required
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            
            {/* Redemption Instructions */}
            <Grid item xs={12} md={6}>
              <InputField
                fullWidth
                label="Redemption Instructions"
                multiline
                rows={isMobile ? 3 : 4}
                value={formData.redemption_instructions}
                onChange={(e) => setFormData({ ...formData, redemption_instructions: e.target.value })}
                InputProps={{
                  startAdornment: <Info size={isMobile ? 16 : 18} style={{ marginRight: 10, marginTop: 15, color: '#797D7F' }} />,
                }}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <InputField
                fullWidth
                label="Description"
                multiline
                rows={isMobile ? 3 : 4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                error={!!errors.description}
                helperText={errors.description}
                required
                InputProps={{
                  startAdornment: <AlignLeft size={isMobile ? 16 : 18} style={{ marginRight: 10, marginTop: 15, color: '#797D7F' }} />,
                }}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            
            {/* SMS Content */}
            <Grid item xs={12}>
              <InputField
                fullWidth
                label="SMS Content"
                multiline
                rows={isMobile ? 2 : 3}
                value={formData.sms_content}
                onChange={(e) => setFormData({ ...formData, sms_content: e.target.value })}
                error={!!errors.sms_content}
                helperText={errors.sms_content || "This message will be sent to users when they subscribe to the deal"}
                required
                InputProps={{
                  startAdornment: <MessageSquare size={isMobile ? 16 : 18} style={{ marginRight: 10, marginTop: 15, color: '#797D7F' }} />,
                }}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
          </Grid>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            mt: { xs: 2, md: 3 },
            flexDirection: { xs: isMobile ? 'column' : 'row', md: 'row' },
            gap: { xs: 2, md: 0 }
          }}>
            <Button 
              onClick={handleBack}
              sx={{ 
                color: '#666', 
                textTransform: 'none',
                order: { xs: isMobile ? 2 : 1, md: 1 }
              }}
            >
              Back
            </Button>
            <GradientButton 
              onClick={handleNext}
              endIcon={<motion.div animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>→</motion.div>}
              fullWidth={isMobile}
              sx={{ order: { xs: isMobile ? 1 : 2, md: 2 } }}
            >
              Continue to Finalize
            </GradientButton>
          </Box>
        </CardContent>
      </StyledCard>
    );
  };

  // Step 4: Finalize
  const renderFinalize = () => {
    return (
      <StyledCard 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          {!isMobile && (
            <>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Finalize & Publish
              </Typography>
              <Divider sx={{ mb: 3 }} />
            </>
          )}

          {/* Image Upload */}
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12}>
              <FormSection
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Upload size={isMobile ? 16 : 20} style={{ color: '#FF6C6C' }} />
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="600" 
                    sx={{ ml: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}
                  >
                    Deal Image
                  </Typography>
                </Box>
                
                {imagePreview ? (
                  <Box sx={{ 
                    position: 'relative', 
                    width: '100%', 
                    textAlign: 'center', 
                    mt: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <motion.div
                      whileHover={{ scale: isMobile ? 1.01 : 1.02 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                      style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                    >
                      <img
                        src={imagePreview}
                        alt="Deal preview"
                        style={{ 
                          width: '100%',
                          maxWidth: '600px', 
                          borderRadius: 16, 
                          maxHeight: isMobile ? 200 : 300, 
                          objectFit: 'cover', 
                          boxShadow: '0 8px 20px rgba(0,0,0,0.15)' 
                        }}
                      />
                    </motion.div>
                    <Button
                      color="error"
                      onClick={removeImage}
                      startIcon={<Trash2 size={isMobile ? 14 : 16} />}
                      sx={{ mt: 2, textTransform: 'none', fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                    >
                      Remove Image
                    </Button>
                  </Box>
                ) : (
                  <label htmlFor="deal-image-input">
                    <ImageUploadContainer component={motion.div} whileHover={{ scale: isMobile ? 1.01 : 1.02 }}>
                      <input
                        id="deal-image-input"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleImageChange}
                      />
                      <Upload size={isMobile ? 30 : 40} style={{ color: '#FF6C6C', marginBottom: 16 }} />
                      <Typography 
                        variant={isMobile ? "subtitle1" : "h6"} 
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        Upload Deal Image
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        align="center"
                        sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                      >
                        PNG, JPG up to 2MB
                        <br />
                        Recommended size: 1200 x 800 pixels
                      </Typography>
                      {errors.image && (
                        <Typography color="error" variant="caption" sx={{ mt: 2 }}>
                          {errors.image}
                        </Typography>
                      )}
                    </ImageUploadContainer>
                  </label>
                )}
              </FormSection>
            </Grid>

            {/* Deal Summary */}
            <Grid item xs={12}>
              <FormSection
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Info size={isMobile ? 16 : 20} style={{ color: '#FF6C6C' }} />
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="600" 
                    sx={{ ml: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}
                  >
                    Deal Summary
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">Title</Typography>
                      <Typography 
                        variant="body1" 
                        fontWeight={500}
                        sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                      >
                        {formData.title || '—'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">Categories</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                        {formData.category.length > 0 ? formData.category.map((catId) => {
                          const category = categories.find(cat => cat.id === catId);
                          return (
                            <Chip 
                              key={catId} 
                              label={category ? category.name : catId} 
                              size="small"
                              sx={{ 
                                bgcolor: 'rgba(255, 108, 108, 0.1)', 
                                color: '#FF6C6C',
                                fontWeight: 500
                              }} 
                            />
                          );
                        }) : '—'}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">Duration</Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {formData.start_date} to {formData.end_date}
                        {formData.start_date && formData.end_date && (
                          <span style={{ fontSize: '0.85em', color: '#666', marginLeft: 5 }}>
                            ({calculateDuration(formData.start_date, formData.end_date)} days)
                          </span>
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">Locations</Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {formData.isNationwide ? 'Nationwide' : 
                          formData.location.map((loc, idx) => {
                            let locationText = '';
                            if (loc.region && loc.location) {
                              locationText = `${loc.location === 'Other' ? loc.customLocation : loc.location}, ${loc.region}`;
                            }
                            return <div key={idx}>{locationText || '—'}</div>;
                          })
                        }
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">Original Price</Typography>
                      <Typography variant="body1" fontWeight={500}>
                        ₵{formData.original_price || '—'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">Discounted Price</Typography>
                      <Typography variant="body1" fontWeight={500}>
                        ₵{formData.discounted_price || '—'}
                        {formData.original_price && formData.discounted_price && (
                          <span style={{ 
                            fontSize: '0.85em', 
                            color: '#FF6C6C', 
                            marginLeft: 5,
                            fontWeight: 'bold'
                          }}>
                            ({calculateDiscount(formData.original_price, formData.discounted_price)}% OFF)
                          </span>
                        )}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {formData.phone_number ? `233${formData.phone_number}` : '—'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button 
              onClick={handleBack}
              sx={{ color: '#666', textTransform: 'none' }}
            >
              Back
            </Button>
            <GradientButton 
              type="submit"
              startIcon={<Check />}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Premium Deal"}
            </GradientButton>
          </Box>
        </CardContent>
      </StyledCard>
    );
  };

  if(loading){
    return <Loader />
  }

  return (
    <PageContainer>
      <Navbar page="Create Premium Deal" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mx-auto max-w-7xl"
      >
        <Box 
          sx={{ 
            mb: { xs: 2, md: 4 }, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                background: 'linear-gradient(90deg, #FF6C6C 0%, #FF8F8F 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Create Premium Deal
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Create a new premium deal to attract more customers to your platform
            </Typography>
          </Box>
          
          {!isMobile && (
            <GradientButton 
              type="submit"
              form="premium-deal-form"
              startIcon={<Check />}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Deal"}
            </GradientButton>
          )}
        </Box>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {/* Mobile Step Indicator */}
          {isMobile && (
            <Grid item xs={12}>
              <StyledCard sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Step {activeStep + 1} of {steps.length}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: '8px',
                    bgcolor: '#f0f0f0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${((activeStep + 1) / steps.length) * 100}%`,
                      height: '100%',
                      background: 'linear-gradient(90deg, #FF6C6C 0%, #FF8F8F 100%)',
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {steps[activeStep].description}
                  </Typography>
                </Box>
              </StyledCard>
            </Grid>
          )}

          {/* Left side - Stepper (Desktop only) */}
          {!isMobile && (
            <Grid item xs={12} md={3}>
              <StyledCard 
                component={motion.div}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                sx={{ 
                  p: 3, 
                  position: 'sticky', 
                  top: 24
                }}
              >
                <Stepper 
                  activeStep={activeStep} 
                  orientation="vertical" 
                  sx={{ 
                    '& .MuiStepLabel-root': {
                      padding: '8px 0',
                    },
                    '& .MuiStepConnector-line': {
                      minHeight: 40,
                      ml: 2.75,
                      borderLeftWidth: 2,
                      borderColor: '#f0f0f0'
                    },
                    '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': {
                      borderColor: '#FF6C6C',
                    },
                    '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': {
                      borderColor: '#FF6C6C',
                    }
                  }}
                >
                  {steps.map((step, index) => (
                    <Step key={step.label} completed={activeStep > index}>
                      <StepLabel 
                        StepIconComponent={() => (
                          <StepIcon completed={activeStep > index}>
                            {activeStep > index ? <Check size={20} /> : step.icon}
                          </StepIcon>
                        )}
                      >
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            fontWeight: activeStep === index ? 600 : 400,
                            fontSize: "13px",
                            color: activeStep === index ? '#FF6C6C' : 'text.primary'
                          }}
                        >
                          {step.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontSize:"11px" }}>
                          {step.description}
                        </Typography>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </StyledCard>
            </Grid>
          )}

          {/* Right side - Form */}
          <Grid item xs={12} md={!isMobile ? 9 : 12}>
            <form id="premium-deal-form" onSubmit={handleSubmit}>
              {/* Step content will be here */}
              {renderStepContent()}
            </form>
          </Grid>
          
          {/* Mobile Submit Button */}
          {isMobile && (
            <Grid item xs={12}>
              <Box sx={{ mt: 2, mb: 4 }}>
                <GradientButton
                  type="submit"
                  form="premium-deal-form"
                  startIcon={<Check />}
                  disabled={loading}
                  fullWidth
                  size="large"
                  sx={{ py: 1.5 }}
                >
                  {loading ? "Creating..." : "Create Premium Deal"}
                </GradientButton>
              </Box>
            </Grid>
          )}
        </Grid>
      </motion.div>
    </PageContainer>
  );
};

export default CreatePremiumDeal;
